import React, { useMemo, useState } from "react";

//UI Material
import {
  Button,
  Card,
  Checkbox,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  useMediaQuery,
} from "@mui/material";

//Helpers
import { checkOrderStatusFromProducts } from "../../Helpers/orderFunctions";
import {
  typographyForSubtitles,
  typographyForText,
} from "../../Helpers/typography";
import { fetchWithTokenJSONPost } from "../../Helpers/network";

//Local components
import ProducedItemRow from "./ProducedItemRow";

export default function OrderProductsTable({
  orderData,
  products,
  getOrderData,
  canceled,
  productInfo,
}) {
  // ==========>Component States<==========
  //State for select page
  const [page, setPage] = useState(0);
  //State for manage rows per page
  const [rowsPerPage, setRowsPerPage] = useState(10);
  //State for selected products in bulk finish
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  //State for bulk finish loading
  const [bulkFinishing, setBulkFinishing] = useState(false);
  //Defines the min width screensize to phoneSize
  const phoneSize = !useMediaQuery("(min-width:600px");

  const bulkSelectionEnabled =
    productInfo?.nonRequiredQC === true && !canceled;

  const eligibleProducts = useMemo(
    () =>
      bulkSelectionEnabled
        ? products.filter((product) => product.status !== "FINISHED")
        : [],
    [bulkSelectionEnabled, products]
  );

  const eligibleProductIds = useMemo(
    () => eligibleProducts.map((product) => product.productId),
    [eligibleProducts]
  );

  const allEligibleSelected =
    eligibleProductIds.length > 0 &&
    eligibleProductIds.every((productId) =>
      selectedProductIds.includes(productId)
    );

  const someEligibleSelected =
    !allEligibleSelected &&
    eligibleProductIds.some((productId) =>
      selectedProductIds.includes(productId)
    );

  // ==========> Component Functions <==========
  //Function to manage page selection
  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };
  //Function to control rows per page options
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleProduct = (productId) => {
    setSelectedProductIds((currentSelection) =>
      currentSelection.includes(productId)
        ? currentSelection.filter((id) => id !== productId)
        : [...currentSelection, productId]
    );
  };

  const handleToggleSelectAll = () => {
    if (allEligibleSelected) {
      setSelectedProductIds([]);
      return;
    }

    setSelectedProductIds(eligibleProductIds);
  };

  const orderNumber = Number(
    new URLSearchParams(window.location.search).get("orderId")
  );

  const bulkFinishProducts = async (productIds) => {
    if (productIds.length === 0 || !orderNumber) {
      return;
    }

    setBulkFinishing(true);
    try {
      const requestResponse = await fetchWithTokenJSONPost(
        "/interactions/bulk-finish-products",
        {
          orderNumber,
          productIds,
        }
      );
      const jsonResponse = await requestResponse.json();
      if (jsonResponse.status === "ok") {
        setSelectedProductIds([]);
        getOrderData();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setBulkFinishing(false);
    }
  };

  const handleBulkFinishSelected = () => {
    bulkFinishProducts(selectedProductIds);
  };

  return (
    <Card
      raised
      sx={{
        padding: "15px",
        borderLeft: `8px solid ${
          checkOrderStatusFromProducts(orderData.products, orderData.canceledBy)
            .color
        }`,
      }}
    >
      {typographyForSubtitles("Productos", "secondary")}
      {bulkSelectionEnabled && eligibleProductIds.length > 0 && (
        <Stack
          direction={phoneSize ? "column" : "row"}
          spacing={1}
          alignItems={phoneSize ? "stretch" : "center"}
          justifyContent="space-between"
          sx={{ mb: 1, mt: 1 }}
        >
          {typographyForText(
            selectedProductIds.length > 0
              ? `${selectedProductIds.length} producto(s) seleccionado(s)`
              : `${eligibleProductIds.length} producto(s) pendiente(s)`,
            "bold"
          )}
          <Button
            variant="contained"
            color="secondary"
            size="small"
            sx={{
              color: "white",
              "&.Mui-disabled": {
                color: "action.disabled",
              },
            }}
            disabled={selectedProductIds.length === 0 || bulkFinishing}
            onClick={handleBulkFinishSelected}
          >
            {bulkFinishing ? "Finalizando..." : "Finalizar seleccionados"}
          </Button>
        </Stack>
      )}
      <TableContainer>
        <Table aria-label="orders-table">
          {!phoneSize && (
            <TableHead>
              <TableRow>
                {bulkSelectionEnabled && (
                  <TableCell padding="checkbox" align="center">
                    <Checkbox
                      size="small"
                      checked={allEligibleSelected}
                      indeterminate={someEligibleSelected}
                      disabled={
                        eligibleProductIds.length === 0 || bulkFinishing
                      }
                      onChange={handleToggleSelectAll}
                      inputProps={{ "aria-label": "Seleccionar todos" }}
                    />
                  </TableCell>
                )}
                <TableCell align="center">
                  {typographyForText("ID", "bold")}
                </TableCell>
                <TableCell align="center">
                  {typographyForText("Producto", "bold")}
                </TableCell>
                <TableCell align="center">
                  {typographyForText("S/N", "bold")}
                </TableCell>
                <TableCell align="center">
                  {typographyForText("ComMod", "bold")}
                </TableCell>
                <TableCell align="center">
                  {typographyForText("Estado", "bold")}
                </TableCell>
                <TableCell align="center">
                  {typographyForText("Ver más", "bold")}
                </TableCell>
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            <ProducedItemRow
              getOrderData={getOrderData}
              canceled={canceled}
              products={products.slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage
              )}
              productInfo={productInfo}
              bulkSelectionEnabled={bulkSelectionEnabled}
              selectedProductIds={selectedProductIds}
              onToggleProduct={handleToggleProduct}
              bulkFinishing={bulkFinishing}
            />
          </TableBody>
        </Table>
      </TableContainer>
      {products.length > 10 && (
        <TablePagination
          component="div"
          count={products.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          labelRowsPerPage="Filas"
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Card>
  );
}
